import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Alert,
    CircularProgress,
    Box,
    IconButton,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { addTicketType, editTicketType } from '@openapi/ticket-types/ticket-types';
import { addTicketOption } from '@openapi/ticket-options/ticket-options';
import useNotify from '../../../hooks/useNotify';
import useTicketMatrixData from '../../../hooks/useTicketMatrixData';
import CommonButton from '../../../components/atom/CommonButton';
import AddIcon from '@mui/icons-material/Add';

const PriceMatrixView = ({ showId, readOnly }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const queryClient = useQueryClient();
    const [matrixData, setMatrixData] = useState({});
    const [originalMatrixData, setOriginalMatrixData] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newOptionName, setNewOptionName] = useState('');
    const [newTypeName, setNewTypeName] = useState('');
    const [creatingOption, setCreatingOption] = useState(false);
    const [creatingType, setCreatingType] = useState(false);

    const { ticketTypes, ticketOptions, loading, error } = useTicketMatrixData({
        showId,
        includeSeats: true,
        onError: notifyError,
    });

    useEffect(() => {
        if (ticketTypes.length > 0 && ticketOptions.length > 0) {
            const matrix = {};
            ticketTypes.forEach((type) => {
                matrix[type.id] = {};
                ticketOptions.forEach((option) => {
                    const price = type.priceMatrix[option.name];
                    matrix[type.id][option.name] = price !== undefined ? price / 100 : '';
                });
            });
            setMatrixData(matrix);
            setOriginalMatrixData(matrix);
            setHasUnsavedChanges(false);
        }
    }, [ticketTypes, ticketOptions]);

    useEffect(() => {
        const isDifferent = JSON.stringify(matrixData) !== JSON.stringify(originalMatrixData);
        setHasUnsavedChanges(isDifferent);
    }, [matrixData, originalMatrixData]);

    const updatePrice = useCallback((ticketTypeId, optionName, price) => {
        setMatrixData((prev) => ({
            ...prev,
            [ticketTypeId]: {
                ...prev[ticketTypeId],
                [optionName]: price,
            },
        }));
    }, []);

    const createTicketOption = useCallback(async () => {
        if (!newOptionName.trim()) return;

        setCreatingOption(true);
        try {
            await addTicketOption({
                name: newOptionName.trim(),
                description: newOptionName.trim(),
                showId: Number(showId),
            });
            setNewOptionName('');
            notifySuccess(t('ticketoptions.notifications.added'));
            queryClient.invalidateQueries({ queryKey: ['/ticketoptions'] });
        } catch (error) {
            notifyError(error);
        } finally {
            setCreatingOption(false);
        }
    }, [newOptionName, showId, queryClient, notifySuccess, notifyError, t]);

    const createTicketType = useCallback(async () => {
        if (!newTypeName.trim()) return;

        setCreatingType(true);
        try {
            await addTicketType({
                name: newTypeName.trim(),
                description: '',
                showId: Number(showId),
                priceMatrix: {},
                showInList: true,
                canBuyAlone: true,
                guestVisible: true,
                automaticSeating: 'fullauto',
                seats: [],
            });
            setNewTypeName('');
            notifySuccess(t('tickettypes.notifications.added'));
            queryClient.invalidateQueries({ queryKey: ['/tickettypes'] });
        } catch (error) {
            notifyError(error);
        } finally {
            setCreatingType(false);
        }
    }, [newTypeName, showId, queryClient, notifySuccess, notifyError, t]);

    const saveMatrix = useCallback(async () => {
        setSaving(true);
        try {
            const promises = ticketTypes.map(async (type) => {
                const newPriceMatrix = {};
                ticketOptions.forEach((option) => {
                    const price = matrixData[type.id]?.[option.name];
                    if (price !== '' && price !== null && price !== undefined) {
                        newPriceMatrix[option.name] = Math.floor(parseFloat(price) * 100);
                    }
                });

                return editTicketType(type.id, {
                    name: type.name,
                    nameI18n: type.nameI18n,
                    description: type.description,
                    descriptionI18n: type.descriptionI18n,
                    showId: type.showId,
                    showInList: type.showInList,
                    canBuyAlone: type.canBuyAlone,
                    priceMatrix: newPriceMatrix,
                    guestVisible: type.guestVisible,
                    automaticSeating: type.automaticSeating,
                    customFields: type.customFields,
                    seats: type.seats?.map((seat) => seat.id) || [],
                });
            });

            await Promise.all(promises);
            setOriginalMatrixData(matrixData);
            setHasUnsavedChanges(false);
            notifySuccess(t('tickettypes.matrixSaved'));
        } catch (error) {
            notifyError(error);
        } finally {
            setSaving(false);
        }
    }, [matrixData, ticketTypes, ticketOptions, notifySuccess, notifyError, t]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{t('tickettypes.errorLoadingData')}</Alert>;
    }

    const hasNoTypes = ticketTypes.length === 0;
    const hasNoOptions = ticketOptions.length === 0;

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {hasNoTypes && hasNoOptions && (
                <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { textAlign: 'left' } }}>
                    {t('tickettypes.priceMatrixEmptyState')}
                </Alert>
            )}
            {hasNoTypes && !hasNoOptions && (
                <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { textAlign: 'left' } }}>
                    {t('tickettypes.noTicketTypesHint')}
                </Alert>
            )}
            {!hasNoTypes && hasNoOptions && (
                <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { textAlign: 'left' } }}>
                    {t('tickettypes.noTicketOptionsHint')}
                </Alert>
            )}
            {!hasNoTypes && !hasNoOptions && (
                <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { textAlign: 'left' } }}>
                    {t('tickettypes.priceMatrixViewInfo')}
                </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 160 }}>
                                {t('tickettypes.ticketType')}
                            </TableCell>
                            {ticketOptions.map((option) => (
                                <TableCell key={option.id}>{option.name}</TableCell>
                            ))}
                            {!readOnly && (
                                <TableCell sx={{ borderLeft: '3px solid #edf2f4' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            sx={{ minWidth: 150 }}
                                            placeholder={t('ticketoptions.newTicketOptionName')}
                                            value={newOptionName}
                                            onChange={(e) => setNewOptionName(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && newOptionName.trim()) {
                                                    createTicketOption();
                                                }
                                            }}
                                            disabled={creatingOption}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={createTicketOption}
                                            disabled={!newOptionName.trim() || creatingOption}
                                            color="primary"
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ticketTypes.map((type) => (
                            <TableRow key={type.id}>
                                <TableCell>{type.name}</TableCell>
                                {ticketOptions.map((option) => (
                                    <TableCell
                                        key={option.id}
                                        sx={{
                                            borderLeft: '3px solid #edf2f4',
                                            minWidth: 110,
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            disabled={readOnly}
                                            inputProps={{
                                                min: 0,
                                                step: 0.01,
                                                style: { textAlign: 'right' },
                                            }}
                                            value={
                                                matrixData[type.id]?.[option.name] !== undefined
                                                    ? matrixData[type.id][option.name]
                                                    : ''
                                            }
                                            onChange={(ev) => {
                                                updatePrice(type.id, option.name, ev.target.value);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        $
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                ))}
                                {!readOnly && (
                                    <TableCell sx={{ borderLeft: '3px solid #edf2f4' }}>
                                        {/* Empty cell for new option column */}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {!readOnly && (
                            <TableRow sx={{ borderTop: '3px solid #edf2f4' }}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder={t('tickettypes.newTicketTypeName')}
                                            value={newTypeName}
                                            onChange={(e) => setNewTypeName(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && newTypeName.trim()) {
                                                    createTicketType();
                                                }
                                            }}
                                            disabled={creatingType}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={createTicketType}
                                            disabled={!newTypeName.trim() || creatingType}
                                            color="primary"
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                {ticketOptions.map((option) => (
                                    <TableCell
                                        key={option.id}
                                        sx={{ borderLeft: '3px solid #edf2f4' }}
                                    >
                                        {/* Empty cells for new type row */}
                                    </TableCell>
                                ))}
                                <TableCell sx={{ borderLeft: '3px solid #edf2f4' }}>
                                    {/* Empty cell for intersection */}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {!readOnly && hasUnsavedChanges && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        zIndex: 1,
                        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                    }}
                >
                    <CommonButton
                        fullWidth
                        label={t('common.save')}
                        onClick={saveMatrix}
                        buttonType="primary"
                        disabled={saving}
                    />
                </Box>
            )}
        </Box>
    );
};

export default PriceMatrixView;
